<template>
  <div class="tw-flex tw-h-screen tw-overflow-hidden tw-bg-gray-100">
    <!-- LEFT: Zoekpaneel (25% breedte) -->
    <div class="tw-w-1/4 tw-min-w-[320px] tw-max-w-[480px] tw-flex tw-flex-col tw-border-r tw-border-gray-200 tw-bg-white">
      <!-- Tabs -->
      <div class="tw-flex tw-border-b tw-border-gray-200 tw-flex-shrink-0">
        <button
          @click="activeTab = 'meters'"
          class="tw-flex-1 tw-py-2 tw-text-sm tw-font-medium tw-transition-colors"
          :class="activeTab === 'meters' ? 'tw-border-b-2 tw-border-blue-500 tw-text-blue-600' : 'tw-text-gray-500 hover:tw-text-gray-700'"
        >
          Meters
        </button>
        <button
          v-if="hasPermission(['ERP:get_tenant_address'])"
          @click="activeTab = 'huurders'"
          class="tw-flex-1 tw-py-2 tw-text-sm tw-font-medium tw-transition-colors"
          :class="activeTab === 'huurders' ? 'tw-border-b-2 tw-border-blue-500 tw-text-blue-600' : 'tw-text-gray-500 hover:tw-text-gray-700'"
        >
          Huurders
        </button>
      </div>

      <!-- Tab: Meters -->
      <div v-if="activeTab === 'meters'" class="tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-p-3 tw-gap-3">
        <!-- Search bar with filter and export -->
        <div>
          <div class="tw-flex tw-gap-1.5">
            <input
              v-model="queryInput"
              type="text"
              placeholder="Zoek op adres of eleenanummer..."
              class="tw-flex-1 tw-min-w-0 tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
            />
            <button
              class="tw-relative tw-flex-shrink-0 tw-border tw-rounded-lg tw-px-2 tw-transition-colors"
              :class="activeFilterCount ? 'tw-border-blue-400 tw-text-blue-600 tw-bg-blue-50' : 'tw-border-gray-300 tw-text-gray-500 hover:tw-text-gray-700'"
              title="Filter op bron, woco, status en SIM-provider"
              @click="showFilters = true"
            >
              <filter-icon class="tw-w-4 tw-h-4" />
              <span
                v-if="activeFilterCount"
                class="tw-absolute -tw-top-1.5 -tw-right-1.5 tw-bg-blue-600 tw-text-white tw-text-[10px] tw-leading-none tw-rounded-full tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center"
              >{{ activeFilterCount }}</span>
            </button>
            <button
              class="tw-flex-shrink-0 tw-border tw-border-gray-300 tw-rounded-lg tw-px-2 tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors disabled:tw-opacity-50"
              title="Exporteer alle meters van deze zoekopdracht en filters naar Excel"
              :disabled="isExporting"
              @click="handleExport"
            >
              <span v-if="isExporting" class="tw-block tw-w-4 tw-h-4 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin" />
              <download-icon v-else class="tw-w-4 tw-h-4" />
            </button>
          </div>
        </div>
        <meter-list-filters
          v-if="showFilters"
          :filters="filters"
          @apply="filters = $event; showFilters = false"
          @close="showFilters = false"
        />

        <!-- Resultaten: vul beschikbare ruimte -->
        <div class="tw-overflow-y-auto tw-flex-1 tw-min-h-0 tw-rounded-lg tw-border tw-border-gray-200">
          <div v-if="isLoading" class="tw-p-3 tw-text-sm tw-text-gray-500">Laden...</div>
          <div v-else-if="error" class="tw-p-3 tw-text-sm tw-text-red-500">Fout bij laden</div>
          <table v-else class="tw-w-full tw-text-xs">
            <tbody>
              <tr
                v-for="row in meters"
                :key="row.serial_number"
                @click="redirectToPollingDetail(row)"
                class="tw-cursor-pointer tw-border-b tw-border-gray-100 hover:tw-bg-blue-50 tw-transition-colors"
                :class="{ 'tw-bg-blue-100': row.serial_number === currentId }"
              >
                <td class="tw-px-2 tw-py-1.5">
                  <div class="tw-flex tw-items-center tw-gap-1.5">
                    <span
                      class="tw-w-2 tw-h-2 tw-rounded-full tw-flex-shrink-0"
                      :class="row.alive ? 'tw-bg-green-500' : 'tw-bg-red-500'"
                    />
                    <span class="tw-font-mono tw-font-medium">{{ row.serial_number }}</span>
                    <meter-source-badge :meter="row" small class="tw-ml-auto" />
                  </div>
                  <div class="tw-text-gray-500 tw-pl-3.5">{{ row.street }} {{ row.house_number }}<span v-if="row.city">, {{ row.city }}</span></div>
                </td>
              </tr>
            </tbody>
          </table>
          <div
            v-if="hasNextPage && !isLoading"
            class="tw-p-2 tw-text-center"
          >
            <button
              @click="fetchNextPage()"
              class="tw-text-xs tw-text-blue-500 hover:tw-text-blue-700"
            >
              Meer laden...
            </button>
          </div>
        </div>

        <!-- Vergelijking meters -->
        <div v-if="metersForComparison.length > 0">
          <div class="tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-1">Vergelijking</div>
          <div
            v-for="m in metersForComparison"
            :key="m.serialNumber"
            class="tw-flex tw-items-center tw-justify-between tw-text-xs tw-bg-gray-50 tw-rounded tw-px-2 tw-py-1 tw-mb-1"
          >
            <span class="tw-font-mono">{{ m.serialNumber }}</span>
            <button @click="removeFromComparison(m.serialNumber)" class="tw-text-red-400 hover:tw-text-red-600">✕</button>
          </div>
        </div>
      </div>

      <!-- Tab: Huurders -->
      <div v-if="activeTab === 'huurders'" class="tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-p-3 tw-gap-3">
        <div>
          <input
            v-model="tenantQueryInput"
            type="text"
            placeholder="Zoek tenant email of adres..."
            class="tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
          />
        </div>
        <div class="tw-overflow-y-auto tw-flex-1 tw-min-h-0 tw-rounded-lg tw-border tw-border-gray-200">
          <div v-if="!tenantQueryInput" class="tw-p-3 tw-text-xs tw-text-gray-400">Typ om te zoeken...</div>
          <div v-else-if="isLoadingTenants" class="tw-p-3 tw-text-xs tw-text-gray-500">Laden...</div>
          <table v-else class="tw-w-full tw-text-xs">
            <tbody>
              <tr
                v-for="row in tenants"
                :key="row.client_id"
                class="tw-border-b tw-border-gray-100"
              >
                <td class="tw-px-2 tw-py-1.5">
                  <div class="tw-font-medium">{{ row.full_name }}</div>
                  <div class="tw-text-gray-500">{{ row.street }} {{ row.house_number }}</div>
                  <div class="tw-flex tw-gap-1 tw-mt-1">
                    <button
                      v-if="row.address_id"
                      class="tw-bg-red-500 tw-text-white tw-rounded tw-px-1 tw-py-0.5 tw-text-xs"
                      @click="handleUnlinkAddress(row.client_id)"
                    >
                      Unlink
                    </button>
                    <button
                      v-else
                      class="tw-bg-green-500 tw-text-white tw-rounded tw-px-1 tw-py-0.5 tw-text-xs"
                      @click="tenantDetail = row"
                    >
                      Link
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- RIGHT: Detail paneel -->
    <div class="tw-flex-1 tw-flex tw-flex-col tw-overflow-y-auto">
      <div v-if="!$route.params.id" class="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-gray-400">
        Selecteer een meter uit de lijst
      </div>
      <polling-detail
        v-else
        :metersForComparison="metersForComparison"
        @addComparison="addToComparison"
      />
    </div>

    <LinkAddressModal
      v-if="!!tenantDetail"
      :tenant-detail="tenantDetail"
      @close="tenantDetail = null"
    />
  </div>
</template>

<script setup>
import { READINGS_API_BASE, meterListParams } from "@/components/Polling/api"
import { reactive, computed, ref } from 'vue'
import { watchDebounced } from '@vueuse/core'
import { useInfiniteQuery, useQueryClient, useMutation } from '@tanstack/vue-query'
import { actions } from '@/store'
import { notifications } from '@/util'
import fetchData from '@/util/fetch'
import PollingDetail from '@/components/Polling/PollingDetail.vue'
import MeterSourceBadge from '@/components/Polling/MeterSourceBadge.vue'
import MeterListFilters from '@/components/Polling/MeterListFilters.vue'
import { exportMeters } from '@/components/Polling/meterExport'
import { FilterIcon, DownloadIcon } from '@vue-hero-icons/outline'
import { useRouter, useRoute } from 'vue-router/composables'
import LinkAddressModal from '@/components/Polling/LinkAddressModal.vue'
import { hasPermission } from '@/logic/common/Permissions'

const activeTab = ref('meters')
const queryInput = ref('')
const tenantQueryInput = ref('')
const router = useRouter()
const route = useRoute()
const tenantDetail = ref(null)

const currentId = computed(() => route.params?.id)

const search = reactive({ query: '' })
const tenantSearch = reactive({ query: '' })

watchDebounced(queryInput, () => { search.query = queryInput.value }, { debounce: 250 })
watchDebounced(tenantQueryInput, () => { tenantSearch.query = tenantQueryInput.value }, { debounce: 250 })

const metersForComparison = ref([])

const redirectToPollingDetail = (row) => {
  if (row.serial_number === currentId.value) return
  router.push({ name: 'polling-detail', params: { id: row.serial_number } })
}

const addToComparison = (serialNumber, address) => {
  const existingIndex = metersForComparison.value.findIndex(m => m.serialNumber === serialNumber)
  if (existingIndex > -1) return
  if (metersForComparison.value.length >= 4) metersForComparison.value.shift()
  metersForComparison.value.push({ serialNumber, address })
}

const removeFromComparison = (serialNumber) => {
  const idx = metersForComparison.value.findIndex(m => m.serialNumber === serialNumber)
  if (idx > -1) metersForComparison.value.splice(idx, 1)
}

const perPage = 50

// The filters are the query parameters of /meters: source, c4a_account, customer_ids, alive, sim_provider
const filters = ref({})
const showFilters = ref(false)
// 'C4A Staedion' sets both source and c4a_account: counts as one filter
const activeFilterCount = computed(() => Object.keys(filters.value).filter((key) => key !== 'c4a_account').length)

const isExporting = ref(false)
const handleExport = async () => {
  isExporting.value = true
  try {
    const { count, truncated } = await exportMeters({ query: search.query, filters: filters.value })
    notifications.addNotification({
      message: truncated
        ? `Export bevat de eerste ${count} meters: verfijn de filters voor de rest`
        : `${count} meters geëxporteerd`,
      type: truncated ? 'warning' : 'success',
    })
  } catch (e) {
    notifications.addNotification({ message: `Export mislukt: ${e?.message || e?.detail || e}`, type: 'danger' })
  } finally {
    isExporting.value = false
  }
}

const getList = async ({ pageParam = 1 }) => {
  const urlParams = meterListParams(filters.value, { offset: (pageParam - 1) * perPage, limit: perPage })
  if (search.query) urlParams.append('query', search.query)
  try {
    return await fetchData(`${READINGS_API_BASE}/meters?${urlParams}`)
  } catch (error) {
    const message = error?.message ?? String(error)
    notifications.addNotification({ message: `Fout bij laden meters: ${message}`, type: 'danger' })
    throw new Error('Failed to get list of meters: ' + message)
  }
}

const { data: metersQuery, isLoading, error, fetchNextPage, hasNextPage } = useInfiniteQuery({
  queryKey: computed(() => ['meters', search.query, filters.value]),
  queryFn: getList,
  refetchOnWindowFocus: false,
  keepPreviousData: true,
  getNextPageParam: (_, allPages) =>
    allPages[allPages.length - 1]?.data?.length === perPage ? allPages.length + 1 : undefined,
})

const meters = computed(() => metersQuery.value?.pages.flatMap((p) => p.data) ?? [])

const getTenants = async ({ pageParam = 1 }) => {
  const urlParams = new URLSearchParams({ offset: (pageParam - 1) * perPage, limit: perPage })
  if (tenantSearch.query) urlParams.append('search', tenantSearch.query)
  try {
    const result = await fetch(`/eleena/api/v1/eleena-addresses?${urlParams}`, {
      headers: { Authorization: `Bearer ${actions.auth.readToken()}` },
    })
    if (!result.ok) return
    return await result.json()
  } catch (error) {
    throw new Error('Failed to get tenants: ' + error.message)
  }
}

const { data: tenantsQuery, isLoading: isLoadingTenants } = useInfiniteQuery({
  queryKey: computed(() => ['tenants', tenantSearch.query]),
  queryFn: getTenants,
  refetchOnWindowFocus: false,
  enabled: computed(() => !!tenantSearch.query),
})

const tenants = computed(() => tenantsQuery.value?.pages.flatMap((p) => p.items ?? []) ?? [])

const queryClient = useQueryClient()

const { mutate: handleUnlinkAddress } = useMutation({
  mutationFn: async (clientId) => {
    const result = await fetch(`/eleena/api/v1/eleena-clients/${clientId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address_id: null }),
    })
    return result
  },
  onSuccess: () => {
    notifications.addNotification({ message: 'Client ontkoppeld', type: 'success' })
    queryClient.invalidateQueries(['tenants'])
  },
  onError: (e) => {
    notifications.addNotification({ message: `Fout bij ontkoppelen: ${e}`, type: 'danger' })
  },
})
</script>
